import "./SummaryBox.css";
import { useNavigate } from "react-router-dom";

export interface OverviewStats {
  total_repos: number;
  total_images: number;
  total_vulnerabilities: number;
  avg_vulns_per_image: number;
}

type Props = {
  stats: OverviewStats | null;
  loading: boolean;
  error: string | null;
};

function QMark({ label, tip }: { label: string; tip: string }) {
  return (
    <button
      type="button"
      className="qmark"
      aria-label={label}
      data-tip={tip}
      title={tip}
      onClick={(e) => e.preventDefault()}
    >
      <span aria-hidden="true">?</span>
    </button>
  );
}

const SummaryBox = ({ stats, loading, error }: Props) => {
  const navigate = useNavigate();

  if (loading && !stats) {
    return <div className="summaryBox-loading">Loading…</div>;
  }

  if (error && !stats) {
    return <div className="summaryBox-error">Error: {error}</div>;
  }

  const s: OverviewStats = stats || {
    total_repos: 0,
    total_images: 0,
    total_vulnerabilities: 0,
    avg_vulns_per_image: 0,
  };

  const formatNumber = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : `${n}`;

  const items = [
    {
      key: "repos",
      label: "Total Image Repositories",
      value: formatNumber(s.total_repos),
      description:
        "Number of distinct image repositories in the Level-0 dataset.",
    },
    {
      key: "images",
      label: "Total Image Versions",
      value: formatNumber(s.total_images),
      description:
        "Number of unique container image versions across all repositories.",
    },
    {
      key: "vulns",
      label: "Total Vulnerabilities",
      value: formatNumber(s.total_vulnerabilities),
      description:
        "Total count of vulnerabilities found across all image versions.",
    },
    {
      key: "avg",
      label: "Average Vulnerabilities per Image Version",
      value: Number.isFinite(s.avg_vulns_per_image)
        ? s.avg_vulns_per_image.toFixed(1)
        : "0.0",
      description: "Average CVEs per image version.",
    },
  ] as const;

  const onCardClick = (key: string) => {
    if (key === "images") navigate("/versions/all");
    if (key === "vulns") navigate("/vulnerabilities/all");
  };

  return (
    <div className="summaryBox-container">
      {items.map((item) => {
        const clickable = item.key === "images" || item.key === "vulns";
        return (
          <div
            key={item.key}
            className={`summaryBox-card ${
              clickable ? "summaryBox-card--clickable" : ""
            }`}
            onClick={() => clickable && onCardClick(item.key)}
            role={clickable ? "button" : undefined}
            aria-label={
              clickable
                ? `${item.label}. ${item.description} Click to view details.`
                : `${item.label}. ${item.description}`
            }
            tabIndex={clickable ? 0 : -1}
            onKeyDown={(e) => {
              if (!clickable) return;
              if (e.key === "Enter" || e.key === " ") onCardClick(item.key);
            }}
          >
            <h2>{item.value}</h2>
            <div className="summaryBox-labelRow">
              <p>{item.label}</p>
              <QMark label={`${item.label} help`} tip={item.description} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SummaryBox;
